import { CoAuthorsEditor } from ".";
import { List } from "immutable";
import _ from "lodash";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import { Paths } from "../../../../constants";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { TinymceOptions } from "../../../../options";
import { Api } from "../../../../services/Api";
import { ReactHelper, Utility } from "../../../../utils";

interface OwnProps {
    abstract?: M.Abstract.Abstract;
    api: Api;
    eventId: number;
    loadingTypes: boolean;
    types: List<M.Abstract.AbstractType>;
    onSubmit: (model: Forms.AdminAbstract.AbstractAuthorshipData, body: any) => void;
}

interface OwnState {
    changeProfilePhoto: boolean;
    showTypeChangeWarning: boolean;
    typeChangeAccepted: boolean;
}

export class AuthorshipForm extends ComponentWithValidatedForm<OwnProps, OwnState> {

    private _searchTimeout: NodeJS.Timer;

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.AdminAbstract.AbstractAuthorship();
        this.state = {
            ...this.getInitialState(formModel),
            changeProfilePhoto: false,
            showTypeChangeWarning: false,
            typeChangeAccepted: false,
        };
    }

    public componentDidMount() {
        this.initialChecks();
    }

    public componentDidUpdate(prevProps: Readonly<OwnProps>) {
        this.initialChecks(prevProps);
    }

    public setFormData(abstract: M.Abstract.Abstract) {
        const formModel = this.getFormModel() as Forms.AdminAbstract.AbstractAuthorship;
        formModel.data.abstractId = abstract.id;
        formModel.data.keywords = abstract.keywords.map((x) => ({
            value: x,
            label: x,
        })),
        formModel.data.typeId = abstract.typeId;
        formModel.data.title = abstract.title || "";
        formModel.data.studyGroup = abstract.studyGroup || "";

        formModel.data.authors = abstract.authors.map((x, i) => ({
            deleted: false,
            displayProfilePicture: x.showProfilePicture,
            email: x.email,
            firstName: x.firstName || "",
            id: x.id,
            index: i,
            isMember: x.isMember,
            lastName: x.lastName || "",
            legacyMemberId: x.legacyMemberId || undefined,
            phone: x.phone
                ? Forms.Generic.PhoneModelToForm(x.phone)
                : { ...Forms.Generic.DefaultPhoneNumber },
            profilePicture: x.profilePicture,
            roleId: x.roleId,
            sort: x.sort,
            title: x.title,
            userId: x.userId,
        }));

        this.setState({
            ...this.state,
            formModel,
        });
    }

    public render() {
        const isNew = !this.props.abstract;
        // const isMember = this.props.submittingUser.isMember;
        // const formModel = this.getFormModel() as Forms.AdminAbstract.Authorship;
        const cancelLink = this.props.abstract
            ? Paths.AdminAbstractDetails(this.props.eventId, this.props.abstract.guid)
            : Paths.AdminAbstractList(this.props.eventId);

        const types: M.Editors.SelectItem[] = this.props.types
            .filter((x: M.Abstract.AbstractType) => !x.isHiddenOnForm)
            .map((x: M.Abstract.AbstractType) => {
                const isDisabled = false; // !isMember && x.membersOnly;

                return {
                    disabled: isDisabled,
                    id: x.id,
                    render: x.name + (isDisabled ? "*" : ""),
                    selected: false,
                };
            })
            .toArray();

        const typeFieldLabel = this.getFieldLabel("typeId");
        const typeErrors = this.getFieldErrors("typeId");
        const typeField = isNew || this.state.typeChangeAccepted
            ? (this.props.loadingTypes
                ? <Comp.Editors.FormField name="" label={typeFieldLabel} required><Comp.Generic.LoadingIcon /> Loading abstract types...</Comp.Editors.FormField>
                : <Comp.Editors.RadioOrCheckListField formComponent={this} name="typeId" items={types} type="radio" />)
            : <Comp.Editors.FormField name="" label={typeFieldLabel} required>
                <p>
                    {(this.props.abstract && this.props.abstract.type) || "Unknown presentation format"}{" "}
                    <span className="mute">
                        <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleTypeChangeWarning(); }}>(Change)</a>
                    </span>
                </p>

                <Comp.Editors.FormFieldErrors errors={typeErrors} />

                <input type="hidden" name="typeId" />

                <BS.Collapse in={this.state.showTypeChangeWarning}>
                    <BS.Alert bsStyle="warning">
                        <h3><Comp.Generic.Icon icon="warning" /> Warning</h3>
                        <p>Changing the presentation format will require re-entering abstract details as well as re-uploading any media.</p>
                        <p>
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleTypeChange(true); }}>Accept</a>
                            {" | "}
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleTypeChange(false); }}>Cancel</a>
                        </p>
                    </BS.Alert>
                </BS.Collapse>
            </Comp.Editors.FormField>;

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onInputChange={(inputName) => this.onInputChangeHandler(inputName)}
            onValidSubmit={async (model) => this.handleSubmit(model)}
            schema={this.state.formModel.rules}
            ref={(ref: any) => this.form = ref}
        >
            {typeField}

            <Comp.Editors.RichTextField
                formComponent={this}
                name="title"
                description={<p className="mute">The title should appropriately describe the essence of the study. Capitalize major words within the title.</p>}
                options={TinymceOptions.specialCharacters({
                    characterlimit_max: 200,
                    statusbar: true,
                }, "charactercount characterlimit")}
            />

            <CoAuthorsEditor
                formComponent={this}
                api={this.props.api}
            />

            <Comp.Editors.SelectField
                formComponent={this}
                isAsync
                description={<p>Please include three to five keywords that describe your abstract. These will be used to help attendees search by topic area/relevance.
                    Choose keywords from the suggest list or enter your own one at a time. <strong>Press enter after each keyword—do not separate with commas.</strong></p>}
                name="keywords"
                select={{
                    // cache: false,
                    arrowRenderer: () => null,
                    ignoreCase: false,
                    loadOptions: (search: string) => this.searchTags(search),
                    multi: true,
                    placeholder: "Examples: Retinablastoma Macular pucker Wet AMD",
                    searchPromptText: "Start typing and select from suggested or enter new tags",
                }}
            />

            <Comp.Editors.RichTextField
                formComponent={this}
                name="studyGroup"
                description={<p className="mute">Please include Study Group if these data entail clinical trial data. Example: MARINA, EVEREST, VIVID, DRCR.net</p>}
            />

            <Comp.Editors.SubmitField cancelLink={cancelLink} label="Save" showProcessing={this.state.formProcessing} />

        </Comp.Validation.ValidatedForm>;
    }

    private initialChecks(prevProps?: Readonly<OwnProps>) {
        if (this.props.abstract && (this.state.formModel.data as Forms.AdminAbstract.AbstractAuthorshipData).abstractId === 0)
            this.setFormData(this.props.abstract);
    }

    private onInputChangeHandler(inputName: string) {
        // if (inputName === "typeId")
        //     this.checkAbstractTypeStats();
        if (inputName === "keywords") {
            const model = this.getFormModelData<Forms.Abstract.ComposeAuthorshipData>();
            if (model.keywords) {
                const allAsStr = model.keywords.map((x: M.Generic.ReactSelectOption) => x.value).join(", ");
                const keywords = allAsStr.replace(/,\s{1,}/, ",").split(",").map((x) => x.trim()).filter((x) => x.length > 0);
                model.keywords = [];
                for (const t of keywords)
                    if (!_.find(model.keywords, (x: M.Generic.ReactSelectOption) => (x.value.toString()).toLowerCase() === t.toLowerCase()))
                        model.keywords.push(Utility.toReactSelectOption({}, t, t));
                this.updateFormModelData(model);
            }
        }
    }

    private handleSubmit(model: Forms.AdminAbstract.AbstractAuthorshipData) {
        const authors = model.authors
            .filter((x) => !(x.id <= 0 && x.deleted))
            .map((x) => ({
                deleted: x.deleted,
                displayProfilePicture: M.Abstract.AbstractAuthorRoles.isPrimary(x) && x.displayProfilePicture,
                id: x.id,
                userId: x.userId,
                email: x.email,
                firstName: x.firstName,
                lastName: x.lastName,
                degrees: x.title,
                phone: M.Abstract.AbstractAuthorRoles.isPrimary(x) && x.phone != null
                    ? {
                        number: x.phone.number,
                        countryCode: (x.phone.countryCode as any).value,
                    }
                    : null,
                roleId: x.roleId,
                sort: x.sort,
            }));

        const body = {
            abstractId: model.abstractId,
            authors,
            eventId: this.props.eventId,
            guid: this.props.abstract ? this.props.abstract.guid : undefined,
            studyGroup: model.studyGroup,
            tags: (model.keywords || []).map((x: M.Generic.ReactSelectOption) => x.label),
            title: Utility.decodeHtml(model.title),
            typeId: model.typeId,
        };

        this.props.onSubmit(model, body);
    }

    private searchTags(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = { queryParams: { search, max: 10 }};

                // for adding new value
                const newTag: M.Tag.Tag & M.Generic.ReactSelectOption =
                    Utility.toReactSelectOption({ id: 0, statusId: 0, name: search }, search, search);

                this.props.api.callApi<M.Tag.Tag[]>(Api.endpoints.tag.tags, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            let options = result.data.map((tag) => {
                                return Utility.toReactSelectOption(tag, tag.name, tag.name);
                            });

                            const newTagMatch = _.find(options, (tag) => {
                                return tag.name.toLowerCase().trim() === newTag.label.toLowerCase().trim();
                            });

                            if (newTagMatch === undefined)
                                options = [newTag, ...options];

                            options = [...options];
                            resolve({ options });
                        }
                        else
                            console.error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }

    private toggleTypeChange(isAccepted: boolean) {
        this.setState({
            ...this.state,
            showTypeChangeWarning: !this.state.showTypeChangeWarning,
            typeChangeAccepted: isAccepted,
        });
    }

    private toggleTypeChangeWarning() {
        this.setState({
            ...this.state,
            showTypeChangeWarning: !this.state.showTypeChangeWarning,
        });
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/edit/components/AuthorshipForm.tsx